// Chờ backend tạo xong PDF biên bản rồi tải về thành object URL.
// Tách từ khuôn sẵn có của AgreementPdfModal để modal vùng ký dùng lại.
import { AGREEMENT_PDF_STATUS_GENERATED } from "constants/AgreementReportConstant";
import {
  useDownloadAgreement,
  useGetAgreementInfo,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import { useEffect, useRef, useState } from "react";

// Backend tạo file PDF bất đồng bộ nên phải hỏi lại tới khi xong
const DELAY_CHECK_PDF = 3000;
const MAX_CHECK_PDF = 10;

/** Biên bản (dữ liệu agreement-info) đã tạo xong file PDF, tải về được */
export const isAgreementPdfReady = agreementData =>
  agreementData?.pdf_status === AGREEMENT_PDF_STATUS_GENERATED &&
  !!agreementData?.agreement_id;

/**
 * @param {object} params
 * @param {boolean} params.isOpen chỉ chạy khi modal đang mở
 * @param {number} params.invoiceId
 * @param {string} params.typeReport
 * @param {number} [params.agreementId] biên bản đang xem (một loại lập được
 * nhiều lần)
 * @param {object} [params.agreementInfo] biên bản modal cha vừa nạp; PDF đã
 * xong thì tải luôn, khỏi gọi agreement-info lần nữa
 */
export const useAgreementPdfUrl = ({
  isOpen,
  invoiceId,
  typeReport,
  agreementId,
  agreementInfo = null,
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isWaitTooLong, setIsWaitTooLong] = useState(false);

  const checkCountRef = useRef(0);
  const timerRef = useRef(null);

  const { mutate: mutateDownloadAgreement } = useDownloadAgreement({
    onSuccess: response => {
      setPdfUrl(URL.createObjectURL(response.data));
    },
    onError: () => {
      setIsError(true);
    },
  });

  const { mutate: mutateGetAgreementInfo } = useGetAgreementInfo({
    onSuccess: response => {
      const agreementData = response.data;

      // Đã tạo xong file thì tải về hiển thị
      if (isAgreementPdfReady(agreementData)) {
        mutateDownloadAgreement({ agreement_id: agreementData.agreement_id });
        return;
      }

      // Chưa xong: chờ rồi hỏi lại, quá số lần cho phép thì để user tự thử lại
      if (checkCountRef.current >= MAX_CHECK_PDF) {
        setIsWaitTooLong(true);
        return;
      }

      checkCountRef.current += 1;
      timerRef.current = setTimeout(() => {
        mutateGetAgreementInfo({
          id: invoiceId,
          type: typeReport,
          agreement_id: agreementId,
        });
      }, DELAY_CHECK_PDF);
    },
    onError: () => {
      setIsError(true);
    },
  });

  const retry = () => {
    clearTimeout(timerRef.current);
    checkCountRef.current = 0;
    setPdfUrl(null);
    setIsError(false);
    setIsWaitTooLong(false);
    mutateGetAgreementInfo({
      id: invoiceId,
      type: typeReport,
      agreement_id: agreementId,
    });
  };

  // Mở thì hỏi trạng thái file, đóng thì xóa sạch
  useEffect(() => {
    if (isOpen && invoiceId) {
      // Có sẵn biên bản đã tạo xong PDF thì tải luôn. "Thử lại" vẫn hỏi API.
      if (isAgreementPdfReady(agreementInfo)) {
        mutateDownloadAgreement({ agreement_id: agreementInfo.agreement_id });
        return;
      }
      retry();
    } else {
      clearTimeout(timerRef.current);
      setPdfUrl(null);
      setIsError(false);
      setIsWaitTooLong(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Dừng hẹn giờ khi component bị gỡ
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Thu hồi object URL cũ để không rò bộ nhớ
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return { pdfUrl, isError, isWaitTooLong, retry };
};
